// Tooltip.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

class Tooltip extends React.PureComponent {
  state = {
    isActive: false,
  };
  nextTick = null;
  autoCloseTimer = null;

  closeHint = () => {
    const { onHintClosed } = this.props;

    this.setState({ isActive: false });

    document.removeEventListener('click', this.closeHint);
    clearTimeout(this.nextTick);

    onHintClosed();
  };

  openHint = () => {
    const { onHintOpened } = this.props;
    const { isActive } = this.state;

    if (isActive) {
      return;
    }
    this.setState({
      isActive: true,
    });

    onHintOpened();
  };

  handleClick = () => {
    const { isActive } = this.state;
    if (isActive) {
      this.closeHint();
    } else {
      this.openHint();

      // Fix Tooltip been closed immediately
      this.nextTick = setTimeout(() => {
        document.addEventListener('click', this.closeHint);
      });
    }
  };

  handleTouchHoverFallback = () => {
    const { isActive } = this.state;
    const { mobileHoverDelaySeconds } = this.props;
    clearTimeout(this.nextTick);
    if (!isActive) {
      this.openHint();
    }
    this.nextTick = setTimeout(() => {
      this.closeHint();
    }, mobileHoverDelaySeconds * 1000);
  };

  renderHint() {
    const { position, renderHint, style } = this.props;
    const { isActive } = this.state;
    return (
      <Hint
        isActive={isActive}
        position={position}
        onClick={event => {
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
        }}
        style={style.hint}
      >
        {renderHint()}
      </Hint>
    );
  }

  handleAutoCloseHint = () => {
    const { trigger, disabled, autoCloseDelaySeconds } = this.props;
    if ('auto' === trigger && !disabled) {
      this.openHint();
      this.autoCloseTimer = setTimeout(() => {
        this.closeHint();
      }, autoCloseDelaySeconds * 1000);
      document.addEventListener('click', this.closeHint);
    }
  };

  componentDidMount() {
    this.handleAutoCloseHint();
  }

  componentWillUnmount() {
    clearTimeout(this.nextTick);
    clearTimeout(this.autoCloseTimer);
  }

  render() {
    const { children, trigger, disabled, style } = this.props;
    return (
      <StyledTooltip
        // By event
        onPointerDown={event => {
          if (disabled) return;
          if (event.pointerType === 'mouse' && trigger === 'click') {
            return this.handleClick();
          }
          // when use hover tooltip should handle mobile click event
          if (
            (event.pointerType === 'touch' || event.pointerType === 'pen') &&
            trigger === 'hover'
          ) {
            return this.handleTouchHoverFallback();
          }
        }}
        onPointerEnter={event => {
          if (!disabled && event.pointerType === 'mouse') this.openHint();
        }}
        onPointerLeave={event => {
          if (!disabled && event.pointerType === 'mouse') this.closeHint();
        }}
        style={style.wrapper}
      >
        {children}
        {this.renderHint()}
      </StyledTooltip>
    );
  }
}

Tooltip.propTypes = {
  position: PropTypes.oneOf([
    'top',
    'top-left',
    'top-right',
    'right',
    'bottom',
    'bottom-center',
    'left',
  ]),
  trigger: PropTypes.oneOf(['click', 'hover', 'auto']),
  children: PropTypes.node,
  renderHint: PropTypes.func,
  onHintOpened: PropTypes.func,
  onHintClosed: PropTypes.func,
  disabled: PropTypes.bool,
  style: PropTypes.object,
  autoCloseDelaySeconds: PropTypes.number,
  mobileHoverDelaySeconds: PropTypes.number,
};

Tooltip.defaultProps = {
  position: 'left',
  trigger: 'click',
  renderHint: () => null,
  onHintOpened: () => {},
  onHintClosed: () => {},
  disabled: false,
  style: {},
  autoCloseDelaySeconds: 5,
  mobileHoverDelaySeconds: 2,
};

const StyledTooltip = styled.div`
  position: relative;
`;

const transformPosition = ({ position }) => {
  switch (position) {
    case 'top':
      return `
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
      `;
    case 'top-right':
      return `
        bottom: 100%;
        right: 0;
      `;
    case 'top-left':
      return `
        bottom: 100%;
        left: 0;
      `;
    case 'right':
      return `
        top: 50%;
        left: 100%;
        transform: translateY(-50%);
      `;
    case 'bottom':
      return 'top: 100%';
    case 'bottom-center':
      return `
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
      `;
    case 'left':
    default:
      return `
        top: 50%;
        right: 100%;
        transform: translateY(-50%);
      `;
  }
};

const showHint = ({ isActive }) =>
  isActive
    ? `
  opacity: 1;
  visibility: visible;
`
    : `
  opacity: 0;
  visibility: hidden;
`;

const Hint = styled.div`
  position: absolute;
  transition: opacity 0.3s;
  ${transformPosition};
  ${showHint};
`;

export default Tooltip;
